/**
 * InboxPage
 *
 * Full-page view for managing all inbox messages with tabs for inbox, archived, and deleted views.
 */

import { useState } from 'react';
import {
  InboxIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useUnifiedInbox, UnifiedInboxView } from '../hooks/useUnifiedInbox';
import { useInboxSelection } from '../hooks/useInboxSelection';
import { UnifiedInboxMessageItem } from '../components/UnifiedInboxMessageItem';
import { UnifiedInboxMessageModal } from '../components/UnifiedInboxMessageModal';
import { InboxBulkActionBar } from '../components/InboxBulkActionBar';
import type { UnifiedInboxItem, InboxSourceFilter, MessagePriority } from '../../../types/app-message.types';
import type { BulkActionType } from '../../../types/inbox.types';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import clsx from 'clsx';

// View tabs configuration
const VIEW_TABS: { id: UnifiedInboxView; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'inbox', label: 'Inbox', icon: InboxIcon },
  { id: 'archived', label: 'Archived', icon: ArchiveBoxIcon },
  { id: 'deleted', label: 'Deleted', icon: TrashIcon },
];

// Priority options for filter
const PRIORITY_OPTIONS: { value: MessagePriority | ''; label: string }[] = [
  { value: '', label: 'All Priorities' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

export function InboxPage() {
  const layoutProps = useLayoutProps();
  const [view, setView] = useState<UnifiedInboxView>('inbox');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<InboxSourceFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<MessagePriority | ''>('');
  const [selectedItem, setSelectedItem] = useState<UnifiedInboxItem | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkLoadingAction, setBulkLoadingAction] = useState<BulkActionType | null>(null);

  const {
    items,
    totalUnreadCount,
    platformUnreadCount,
    appUnreadCount,
    isLoading,
    error,
    hasMore,
    refresh,
    markAsRead,
    markAllAsRead,
    archiveMessage,
    unarchiveMessage,
    softDeleteMessage,
    restoreMessage,
    permanentDeleteMessage,
    bulkAction,
  } = useUnifiedInbox({
    view,
    sourceFilter,
    unreadOnly: showUnreadOnly,
    priority: priorityFilter || undefined,
  });

  // Selection state — auto-clears when view/filters change
  const {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    selectedCount,
    isAllSelected,
  } = useInboxSelection([view, sourceFilter, showUnreadOnly, priorityFilter]);

  const handleBulkAction = async (action: BulkActionType) => {
    const selectedItems = items.filter((item) => selectedIds.has(item.id));
    if (selectedItems.length === 0) return;

    setBulkLoading(true);
    setBulkLoadingAction(action);
    try {
      await bulkAction(action, selectedItems);
      clearSelection();
    } finally {
      setBulkLoading(false);
      setBulkLoadingAction(null);
    }
  };

  const handleMarkAsRead = async (item: UnifiedInboxItem) => {
    await markAsRead(item);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleArchive = async (item: UnifiedInboxItem) => {
    await archiveMessage(item);
  };

  const handleUnarchive = async (item: UnifiedInboxItem) => {
    await unarchiveMessage(item);
  };

  const handleDelete = async (item: UnifiedInboxItem) => {
    await softDeleteMessage(item);
  };

  const handleRestore = async (item: UnifiedInboxItem) => {
    await restoreMessage(item);
  };

  const handlePermanentDelete = async (item: UnifiedInboxItem) => {
    if (window.confirm('This action cannot be undone. Are you sure you want to permanently delete this message?')) {
      await permanentDeleteMessage(item);
    }
  };

  const handleRefresh = async () => {
    await refresh();
  };

  const handleItemClick = (item: UnifiedInboxItem) => {
    setSelectedItem(item);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
  };

  const handleArchiveFromModal = async (item: UnifiedInboxItem) => {
    await archiveMessage(item);
    setSelectedItem(null);
  };

  const handleDeleteFromModal = async (item: UnifiedInboxItem) => {
    await softDeleteMessage(item);
    setSelectedItem(null);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center">
              <InboxIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Inbox</h1>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {totalUnreadCount > 0 ? (
                  <>You have <span className="font-medium text-primary-600">{totalUnreadCount}</span> unread notifications</>
                ) : (
                  'You\'re all caught up!'
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {view === 'inbox' && totalUnreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
              >
                <CheckIcon className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="card rounded-lg shadow-sm mb-4" style={{ borderColor: 'var(--color-border)' }}>
        <nav className="flex" aria-label="Inbox views">
          {VIEW_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = view === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  isActive
                    ? 'border-primary-500 text-primary-600 bg-primary-50'
                    : 'border-transparent hover:border-gray-300'
                )}
                style={!isActive ? { color: 'var(--color-text-secondary)' } : undefined}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Deleted View Warning */}
      {view === 'deleted' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Messages in Deleted</p>
            <p className="text-sm text-amber-700">Messages will be permanently removed after 30 days.</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card rounded-lg shadow-sm p-4 mb-6" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-4 flex-wrap">
          <FunnelIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />

          {/* Source Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Source:</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as InboxSourceFilter)}
              className="text-sm rounded-md focus:ring-primary-500 focus:border-primary-500"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <option value="all">All Messages</option>
              <option value="platform">System ({platformUnreadCount} unread)</option>
              <option value="applications">Applications ({appUnreadCount} unread)</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Priority:</span>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as MessagePriority | '')}
              className="text-sm rounded-md focus:ring-primary-500 focus:border-primary-500"
              style={{ borderColor: 'var(--color-border)' }}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Unread Only (only show for inbox view) */}
          {view === 'inbox' && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                className="rounded text-primary-600 focus:ring-primary-500"
                style={{ borderColor: 'var(--color-border)' }}
              />
              <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>Unread only</span>
            </label>
          )}
        </div>
      </div>

      {/* Messages List */}
      <div className="card rounded-lg shadow-sm overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
        {/* Bulk Action Bar */}
        {items.length > 0 && (
          <InboxBulkActionBar
            selectedCount={selectedCount}
            isAllSelected={isAllSelected(items)}
            onSelectAll={() => selectAll(items)}
            onClearSelection={clearSelection}
            onBulkAction={handleBulkAction}
            view={view}
            isLoading={bulkLoading}
            loadingAction={bulkLoadingAction}
          />
        )}

        {isLoading && items.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-red-500">
            <p className="text-sm font-medium">{error}</p>
            <Button variant="link" size="sm" onClick={handleRefresh} className="mt-2">
              Try again
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12" style={{ color: 'var(--color-text-secondary)' }}>
            {view === 'inbox' && <InboxIcon className="h-12 w-12 mb-3" style={{ color: 'var(--color-text-muted)' }} />}
            {view === 'archived' && <ArchiveBoxIcon className="h-12 w-12 mb-3" style={{ color: 'var(--color-text-muted)' }} />}
            {view === 'deleted' && <TrashIcon className="h-12 w-12 mb-3" style={{ color: 'var(--color-text-muted)' }} />}
            <p className="text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {view === 'inbox' && 'No notifications'}
              {view === 'archived' && 'No archived messages'}
              {view === 'deleted' && 'No deleted messages'}
            </p>
            <p className="text-sm">
              {view === 'inbox' && (showUnreadOnly ? 'No unread notifications' : 'Your inbox is empty')}
              {view === 'archived' && 'Messages you archive will appear here'}
              {view === 'deleted' && 'Messages you delete will appear here'}
            </p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {items.map((item) => (
              <div key={item.id} className="px-2 py-1">
                <UnifiedInboxMessageItem
                  item={item}
                  view={view}
                  onRead={view === 'inbox' ? handleMarkAsRead : undefined}
                  onArchive={view === 'inbox' ? handleArchive : undefined}
                  onUnarchive={view === 'archived' ? handleUnarchive : undefined}
                  onDelete={view !== 'deleted' ? handleDelete : undefined}
                  onRestore={view === 'deleted' ? handleRestore : undefined}
                  onPermanentDelete={view === 'deleted' ? handlePermanentDelete : undefined}
                  onClick={handleItemClick}
                  showActions={true}
                  showCheckbox={true}
                  isSelected={isSelected(item.id)}
                  onToggleSelect={(i) => toggleSelect(i.id)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="border-t px-4 py-3 text-center" style={{ borderColor: 'var(--color-border)' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              isLoading={isLoading}
            >
              Load more
            </Button>
          </div>
        )}
      </div>
      </div>

      {/* Message Detail Modal */}
      <UnifiedInboxMessageModal
        item={selectedItem}
        isOpen={selectedItem !== null}
        onClose={handleCloseModal}
        onArchive={view === 'inbox' ? handleArchiveFromModal : undefined}
        onDelete={view !== 'deleted' ? handleDeleteFromModal : undefined}
      />
    </DashboardLayout>
  );
}

export default InboxPage;
